/** @format */

const AUTH_API_URL = import.meta.env.VITE_AUTH_API_URL;

/** Thrown when a request is rejected because the access and refresh tokens have both expired. */
export class SessionExpiredError extends Error {
	constructor(message = "Your session has expired. Please log in again.") {
		super(message);
		this.name = "SessionExpiredError";
	}
}

type SessionExpiredListener = () => void;

// Dispatched on the window rather than held in module state so subscribers are
// never orphaned by a second copy of this module (e.g. after a Vite HMR update).
const SESSION_EXPIRED_EVENT = "questionbank:session-expired";

/** Subscribe to session expiry; returns an unsubscribe function. */
export const onSessionExpired = (listener: SessionExpiredListener): (() => void) => {
	globalThis.addEventListener(SESSION_EXPIRED_EVENT, listener);
	return () => {
		globalThis.removeEventListener(SESSION_EXPIRED_EVENT, listener);
	};
};

export const reportSessionExpired = (): void => {
	globalThis.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
};

/**
 * Ask the auth API whether the session is still alive. Returns `false` unless
 * `/me` explicitly confirms it: an unreachable or blocked probe means we cannot
 * prove the user is still signed in, and the request we are handling has
 * already been rejected for an auth reason.
 */
const isSessionGone = async (): Promise<boolean> => {
	if (typeof navigator !== "undefined" && navigator.onLine === false) {
		return false;
	}
	try {
		const response = await fetch(`${AUTH_API_URL}/me`, {
			method: "GET",
			credentials: "include",
			cache: "no-store",
		});
		return !response.ok;
	} catch {
		return true;
	}
};

const expire = (): never => {
	reportSessionExpired();
	throw new SessionExpiredError();
};

/**
 * `fetch` for endpoints behind the session cookie. When the request is rejected
 * because the session has expired it notifies subscribers (the AuthProvider
 * shows the re-login dialog) and throws a `SessionExpiredError`.
 */
export const authFetch = async (input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> => {
	let response: Response;

	try {
		response = await fetch(input, { ...init, credentials: "include" });
	} catch (error) {
		// API Gateway returns an authorizer denial without CORS headers, so the
		// browser blocks it and `fetch` rejects instead of exposing the 403.
		if (await isSessionGone()) {
			return expire();
		}
		throw error;
	}

	if (response.status === 401) {
		return expire();
	}

	if (response.status === 403 && (await isSessionGone())) {
		return expire();
	}

	return response;
};
